import type { APIProperties } from "models/properties";
import type { Font, ImageResource } from "models/types/agency-settings";
import { AppLayout } from "components/app-layout";
import { PortalErrorScreen } from "components/portal-error-screen";
import { AgencySettingsProvider } from "contexts/agency-settings-provider";
import { RequireAuthRole } from "contexts/auth-provider";
import { PropertyTenancyProvider } from "contexts/property-tenancy-provider";
import type { Route } from "lib/react-router-dom";
import { LeftNavigation } from "modules/tenant-portal/components/left-navigation";
import { TENANT_PORTAL_PATHS } from "modules/tenant-portal/paths";
import { HandbookScreen } from "modules/tenant-portal/screens/handbook/handbook-screen";
import { InspectionsScreen } from "modules/tenant-portal/screens/inspections/inspections-screen";
import { MaintenanceScreen } from "modules/tenant-portal/screens/maintenance/maintenance-screen";
import { OverviewScreen } from "modules/tenant-portal/screens/overview/overview-screen";
import { PaymentsScreen } from "modules/tenant-portal/screens/payments/payments-screen";
import { RequestMaintenanceScreen } from "modules/tenant-portal/screens/request-maintenance/request-maintenance-screen";
import type { PropertyAgency } from "types/property";
import type { TenantSettingsLoaderData } from "types/tenancy";
import { apiClient } from "utils/api-client";
import { isTokenExpired } from "utils/auth";

export const TENANT_PORTAL_ROUTES: Route[] = [
  {
    id: "tenant-root",
    path: TENANT_PORTAL_PATHS.DEFAULT,
    handle: {
      title: "Tenant",
    },
    loader: async (): Promise<TenantSettingsLoaderData> => {
      if (isTokenExpired()) {
        throw new Response(
          JSON.stringify({
            type: "token_expired",
            title: "Link Expired",
            message: "Your access link has expired.",
          }),
          { status: 401, headers: { "Content-Type": "application/json" } },
        );
      }

      const propertiesResponse = await apiClient.get<{
        data: APIProperties[];
      }>("/tenant-portal/properties");

      if (propertiesResponse.data.length === 0) {
        throw new Response(
          JSON.stringify({
            title: "Tenant doesn't have tenancy.",
            message: "No properties found",
          }),
          { status: 404, headers: { "Content-Type": "application/json" } },
        );
      }

      const currentPropertyTenancyId =
        localStorage.getItem("property_tenancy_id") ?? "";

      // we are getting the first option if currentPropertyTenancyId does not exist on valid propertyTenancyIds
      const propertyTenancyIds = propertiesResponse.data.map((p) =>
        String(p.id),
      );
      const propertyTenancyId = propertyTenancyIds.includes(
        currentPropertyTenancyId,
      )
        ? currentPropertyTenancyId
        : String(propertiesResponse.data[0].id);

      // now that we have the selected propertyTenancyId we are getting the agency details using it
      const agencyResponse = await apiClient.get<PropertyAgency>(
        `/tenant-portal/property-tenancies/${propertyTenancyId}/agency`,
      );

      const displayPicture = agencyResponse.display_picture;
      const primaryLogo: ImageResource | null = displayPicture
        ? {
            id: "external",
            path: displayPicture,
            name: agencyResponse.display_name,
            size: 0,
            mime_type: "image/png",
            width: 0,
            height: 0,
            scale: 1,
            urls: {
              "log.primary": displayPicture,
              "log.secondary": displayPicture,
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
        : null;

      const defaultFont: Font = {
        id: "",
        name: "",
        weight: 16,
        style: "",
        size: 16,
        mime_type: "",
        source: "",
        url: "",
        created_at: "",
        updated_at: "",
      };

      const defaultBranding = {
        agency_name: agencyResponse.display_name,
        primary_logo: primaryLogo,
        secondary_logo: null,
        heading_font_family: defaultFont,
        body_font_family: defaultFont,
        accent_color: "",
        navigation_color: "",
        heading_font: defaultFont,
        body_font: defaultFont,
      };

      return {
        agencySettings: Promise.resolve({
          branding: defaultBranding,
        }),
        tenantProperties: propertiesResponse.data,
      };
    },
    errorElement: <PortalErrorScreen />,
    element: (
      <RequireAuthRole anyOf={["tenant", "agent", "agent_manager"]}>
        <AgencySettingsProvider>
          <PropertyTenancyProvider>
            <AppLayout leftNavigationMenu={<LeftNavigation />} />
          </PropertyTenancyProvider>
        </AgencySettingsProvider>
      </RequireAuthRole>
    ),
    children: [
      {
        index: true,
        Component: OverviewScreen,
        handle: {
          title: "Overview",
        },
      },
      {
        path: TENANT_PORTAL_PATHS.MAINTENANCE,
        Component: MaintenanceScreen,
        handle: {
          title: "Maintenance",
        },
      },
      {
        path: TENANT_PORTAL_PATHS.REQUEST_MAINTENANCE,
        Component: RequestMaintenanceScreen,
        handle: {
          title: "Request Maintenance",
        },
      },
      {
        path: TENANT_PORTAL_PATHS.INSPECTIONS,
        Component: InspectionsScreen,
        handle: {
          title: "Inspections",
        },
      },
      {
        path: TENANT_PORTAL_PATHS.PAYMENTS,
        Component: PaymentsScreen,
        handle: {
          title: "Payments",
        },
      },
      {
        path: TENANT_PORTAL_PATHS.HANDBOOK,
        Component: HandbookScreen,
        handle: {
          title: "Handbook",
        },
      },
    ],
  },
];
